// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import { FormInstance } from 'antd';
import { useLocalMessage } from '@Helpers/';
import UploadAvatar, { getUpdatedAvatar, AVATARB64_DEFAULT } from '@Atoms/UploadAvatar/UploadAvatar';
import FormatPermissions from '@Molecules/Format/FormatPermissions/FormatPermissions';
import { THForm } from '@Molecules/THForm/THForm';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { UserInput, UserOutput } from 'thehive-sdk';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THAutoComplete } from '@Molecules/Inputs/THAutoComplete/THAutoComplete';

import './AccountInfosForm.scss';

interface AccountInfosFormValues {
    avatar?: string;
    name?: string;
    login?: string;
    profile?: string;
    defaultOrganisation?: string;
}

export interface AccountInfosFormProps {
    user?: UserOutput;
    onFinish: (user: Partial<UserInput>) => void;
    disabled: boolean;
    onChange: () => void;
    form: FormInstance;
    setFieldsTouched: (value: boolean) => void;
    fieldsTouched: boolean;
}

export const AccountInfosForm = ({
    user,
    onFinish,
    disabled,
    onChange,
    form,
    fieldsTouched,
    setFieldsTouched,
}: AccountInfosFormProps): ReactElement => {
    const i = useLocalMessage();
    const {
        login = '',
        name = '',
        profile = '',
        permissions = [],
        avatar = '',
        organisations,
        defaultOrganisation,
    } = user || {};

    const defaultValues = {
        login,
        name,
        profile,
        avatar: AVATARB64_DEFAULT,
        defaultOrganisation,
    };

    const onSubmitForm = ({ name, avatar, defaultOrganisation }: AccountInfosFormValues): void => {
        onFinish({
            name,
            avatar: getUpdatedAvatar(avatar),
            defaultOrganisation,
        });
    };

    return (
        <THForm<AccountInfosFormValues>
            name="account-infos-form"
            form={form}
            onFinish={onSubmitForm}
            initialValues={defaultValues}
            onValuesChange={onChange}
            className="account-infos-form"
            submitTouchedValues
        >
            <THForm.Layout
                footer={
                    <FormSubmitButton
                        warning
                        onCancel={() => {
                            form.resetFields();
                            setFieldsTouched(false);
                        }}
                        disabled={disabled}
                        hidden={!fieldsTouched}
                    />
                }
            >
                <THForm.Item label={i('account.avatar')} name="avatar">
                    <UploadAvatar avatar={avatar} name={name} size="small" />
                </THForm.Item>

                <THForm.Item name="login" label={i('account.login')}>
                    <THInput disabled />
                </THForm.Item>

                <THForm.Item name="name" label={i('account.name')}>
                    <THInput placeholder={i('account.name')} />
                </THForm.Item>

                <THForm.Item name="profile" label={i('account.profile')}>
                    <THInput disabled placeholder={i('account.profile')} />
                </THForm.Item>

                <THForm.Item
                    name="defaultOrganisation"
                    label={i('account.organisation.label')}
                    hidden={organisations?.length === 1}
                >
                    <THAutoComplete
                        placeholder={i('account.organisation.placeholder')}
                        options={organisations?.map(({ organisation }) => ({
                            label: organisation,
                            value: organisation,
                        }))}
                    />
                </THForm.Item>

                <THForm.Item label={i('account.permissions')}>
                    <FormatPermissions profile={profile} permissions={permissions} permissionsOnly />
                </THForm.Item>
            </THForm.Layout>
        </THForm>
    );
};
